import { computed, Directive, input, signal } from '@angular/core';
import { hlm } from '@spartan-ng/brain/core';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ClassValue } from 'clsx';
import { injectBrnButtonConfig } from './hlm-button.token';

export const buttonVariants = cva(
  'inline-flex cursor-pointer items-center active:scale-[98%] gap-2 justify-center rounded-lg duration-300 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-[3px] disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default: 'bg-primary-500 text-white hover:bg-primary-700 group-hover:bg-primary-700 focus-visible:ring-primary-200',
        outline: 'border text-gray-700 bg-transparent hover:bg-gray-50 focus-visible:ring-gray-100',
        ghost: 'hover:bg-gray-100 text-gray-600 focus-visible:ring-gray-100',
        link: 'underline-offset-4 hover:underline text-primary !h-fit !p-0 focus-visible:ring-0',
        secondary: 'bg-gray-100 text-gray-600 hover:bg-gray-200/80 focus-visible:ring-gray-50',

        'link-secondary': 'text-gray-600 !h-fit !p-0 focus-visible:ring-0 hover:text-gray-900',
        'outline-primary':
          'border border-primary-300 bg-primary-50 text-primary-700 hover:text-primary-800 hover:bg-primary-100 focus-visible:ring-primary-100',
        'ghost-primary': 'hover:bg-primary-50 text-primary-700 hover:text-primary-800 focus-visible:ring-primary-50',

        //? These are custom variants, not from the design system
        destructive: 'bg-error-600 text-white hover:bg-error-700 focus-visible:ring-error-200',
        success: 'bg-success-500 text-white hover:bg-success-600',
        white: 'bg-white text-gray-700 dark:text-gray-100 hover:bg-white/90',
        transparent: 'bg-transparent text-primary',
        tab: 'text-gray-500 hover:text-primary-600 font-semibold rounded-none border-b-2 border-b-transparent',
        contrast: 'bg-contrast-700 text-white dark:text-black hover:bg-contrast-500 group-hover:bg-contrast-500',
        warning: 'bg-warning-600 text-white hover:bg-warning-700 focus-visible:ring-warning-200',
      },
      size: {
        none: '',
        xs: 'h-8 px-3 rounded-lg text-sm',
        sm: 'h-9 px-4 rounded-lg text-sm',
        default: 'h-10 px-5 rounded-lgx text-sm',
        md: 'h-11 px-5 rounded-lgx',
        lg: 'h-12 px-6 rounded-xl',
        xl: 'h-14 px-7 rounded-xl',
        'icon-xs': 'h-8 w-8 rounded-lg text-sm',
        'icon-sm': 'h-9 w-9 rounded-lg text-sm',
        icon: 'h-10 w-10 rounded-lgx',
        'icon-md': 'h-11 w-11 rounded-lgx',
        'icon-lg': 'h-12 w-12 rounded-xl',
        'icon-xl': 'h-14 w-14 rounded-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);
export type ButtonVariants = VariantProps<typeof buttonVariants>;

@Directive({
  selector: '[hlmBtn]',
  standalone: true,
  exportAs: 'hlmBtn',
  host: {
    '[class]': '_computedClass()',
  },
})
export class HlmButtonDirective {
  private readonly _config = injectBrnButtonConfig();

  private readonly _additionalClasses = signal<ClassValue>('');

  public readonly userClass = input<ClassValue>('', { alias: 'class' });
  public readonly dynamicClass = input<ClassValue>('', { alias: 'ngClass' });

  protected readonly _computedClass = computed(() =>
    hlm(
      buttonVariants({
        variant: this.variant(),
        size: this.size(),
      }),
      this.userClass(),
      this._additionalClasses(),
      this.dynamicClass()
    )
  );

  public readonly variant = input<ButtonVariants['variant']>(this._config.variant);

  public readonly size = input<ButtonVariants['size']>(this._config.size);

  setClass(classes: string): void {
    this._additionalClasses.set(classes);
  }
}
